import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { IGeolocationCoordinates, IMarker, IPolyline, ITooltipModal } from '../../types/interfaces';
import { RootState } from '../store';

// Define a type for the slice state
interface MapState {
  flyTo: any;
  zoom: number;
  bounds: {
    south: number;
    north: number;
    west: number;
    east: number;
  } | null;
  drawMarkers: string;
  markers: Array<IMarker>;
  vehicles: any;
  polylines: Array<IPolyline>;
  markerGroup: Array<any>;
  genericElementsInMap: Array<any>;
  markerSelected: number | null;
  markerSelectedType: string | null;
  location?: IGeolocationCoordinates | null;
  blockMarkerAction?: boolean | null;
  randomState?: number | null;
  tooltipModal?: ITooltipModal;
  onMapClick?: any;
}

// Define the initial state using that type
const initialState: MapState = {
  flyTo: null,
  zoom: 12,
  bounds: null,
  location: null,
  drawMarkers: '',
  markers: [],
  vehicles: [],
  polylines: [],
  markerGroup: [],
  genericElementsInMap: [],
  markerSelected: null,
  markerSelectedType: null,
  blockMarkerAction: false,
  randomState: null,
  tooltipModal: undefined,
  onMapClick: undefined,
};

export const mapSlice = createSlice({
  name: 'map',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    flyTo: (state, action) => {
      state.flyTo = action.payload;
    },
    updateZoom: (state, action) => {
      state.zoom = action.payload;
    },
    updateBounds: (state, action) => {
      state.bounds = action.payload;
    },
    updateLocation: (state, action) => {
      state.location = action.payload;
    },
    updateDrawMarker: (state, action) => {
      state.drawMarkers = action.payload;
    },
    updateMarkers: (state, action) => {
      state.markers = action.payload;
    },
    updateGenericElementsInMap: (state, action) => {
      state.genericElementsInMap = action.payload;
    },
    updateMarkerGroup: (state, action) => {
      state.markerGroup = action.payload;
    },
    addMarkers: (state, action) => {
      state.markers.push(action.payload);
    },
    updateMarkerSelected: (state, action: PayloadAction<number | null>) => {
      state.markerSelected = action.payload;
      state.markers = state.markers.map((marker) => {
        return {
          id: marker.id,
          position: marker.position,
          content: marker.content,
          selected: marker.id === action.payload,
          data: marker.data,
          onClick: marker.onClick,
          type: marker?.type,
          transportMode: marker?.transportMode,
        };
      });
      state.drawMarkers = state.drawMarkers + 'S' + action.payload;
    },
    updateMarkerSelectedType: (state, action) => {
      state.markerSelectedType = action.payload;
    },
    updatePolylines: (state, action) => {
      state.polylines = action.payload;
    },
    updateVehicles: (state, action) => {
      state.vehicles = action.payload;
    },
    toogleBlockMarkerAction: (state, action) => {
      state.blockMarkerAction = action.payload;
    },

    updatePolylinesGeneric: (state, action) => {
      state.polylines = action.payload;
      localStorage.setItem(
        'polylinesGeneric',
        JSON.stringify({ flag: new Date().getTime(), lines: JSON.stringify(state.polylines) }),
      );
    },
    updateRandomState: (state) => {
      state.randomState = Math.random();
    },
    updateTooltipModal: (state, action) => {
      state.tooltipModal = action.payload;
    },
    updateOnMapClick: (state, action) => {
      state.onMapClick = action.payload;
    },
  },
});

export const {
  updateLocation,
  updateZoom,
  updateBounds,
  updateDrawMarker,
  updateMarkers,
  updateMarkerGroup,
  updateMarkerSelected,
  updateMarkerSelectedType,
  updatePolylines,
  toogleBlockMarkerAction,
  addMarkers,
  flyTo,
  updatePolylinesGeneric,
  updateVehicles,
  updateRandomState,
  updateGenericElementsInMap,
  updateTooltipModal,
  updateOnMapClick
} = mapSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const mapState = (state: RootState) => state.map;
export const mapMarKerSelectedState = (state: RootState) => state.map.markerSelected;
export const mapMarKerSelectedTypeState = (state: RootState) => state.map.markerSelectedType;
export const mapMarKersState = (state: RootState) => state.map.markers;
export const mapGenericElements = (state: RootState) => state.map.genericElementsInMap;
export const mapMarKerGroupState = (state: RootState) => state.map.markerGroup;
export const mapBoundsState = (state: RootState) => state.map.bounds;
export const mapZoomState = (state: RootState) => state.map.zoom;
export const mapDrawMarkersState = (state: RootState) => state.map.drawMarkers;
export const mapLocationState = (state: RootState) => state.map.location;
export const mapRandomStateState = (state: RootState) => state.map.randomState;
export const mapTooltipModalState = (state: RootState) => state.map.tooltipModal;
export const mapOnClickState = (state: RootState) => state.map.onMapClick;

export default mapSlice.reducer;
